import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "@kkp/ui";
import { Card } from "@kkp/ui";
import { Input } from "../../ui/Input";
import { listLogs } from "../../api/logs";
import { perClientSeqNo } from "../../lib/seq";
import { toast } from "@kkp/ui";

type AuditTrailRecord = {
  seqId: string;
  timestamp: string;
  actor: string;
  ipAddress: string;
  city: string;
  region: string;
  country: string;
  userAgent: string;
  action: string;
  details: string;
};

export default function LogsPage() {
  const [search, setSearch] = React.useState("");
  const [actionFilter, setActionFilter] = React.useState("all");
  const { data, error } = useQuery<Awaited<ReturnType<typeof listLogs>>, Error>(
    {
      queryKey: ["client-logs"],
      queryFn: () => listLogs({ limit: "200" }),
      staleTime: 0,
      retry: 1,
    },
  );

  React.useEffect(() => {
    if (error) {
      toast.danger("Failed to load audit logs.", { title: "Audit trail" });
    }
  }, [error]);

  const auditTrail: AuditTrailRecord[] = (data?.items ?? []).map((item) => ({
    seqId: String(item.seqId),
    timestamp: item.timestamp,
    actor: item.actor,
    ipAddress: item.ipAddress ?? "",
    city: item.city ?? "",
    region: item.region ?? "",
    country: item.country ?? "",
    userAgent: item.userAgent ?? "",
    action: item.reason,
    details: item.details,
  }));

  // Per-client Seq ID (1..N by creation), independent of the global seq_id.
  const logSeqNo = React.useMemo(
    () => perClientSeqNo(auditTrail.map((r) => r.seqId)),
    [auditTrail],
  );

  const filtered = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return auditTrail.filter((record) => {
      const matchesSearch =
        !query ||
        record.actor.toLowerCase().includes(query) ||
        record.details.toLowerCase().includes(query) ||
        record.action.toLowerCase().includes(query);
      const matchesAction =
        actionFilter === "all" || record.action.toLowerCase() === actionFilter;
      return matchesSearch && matchesAction;
    });
  }, [auditTrail, search, actionFilter]);

  const actionOptions = React.useMemo(() => {
    const actions = Array.from(new Set(auditTrail.map((r) => r.action)));
    return actions.sort();
  }, [auditTrail]);

  return (
    <PageContainer
      title="Audit Trail"
      description="Tracks user activities across the client workspace (excluding navigation)."
      width="full"
    >
      <div className="grid gap-4">
        <Card className="grid gap-3">
          <div className="text-sm font-semibold text-white">Filters</div>
          <div className="grid gap-3 lg:grid-cols-[1.5fr_0.75fr]">
            <Input
              placeholder="Search by actor, action, or details"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:var(--kkp-surface-2)] px-3 py-2.5 text-sm text-white"
            >
              <option
                value="all"
                className="bg-[color:var(--kkp-surface)] text-white"
              >
                All actions
              </option>
              {actionOptions.map((action) => (
                <option
                  key={action}
                  value={action.toLowerCase()}
                  className="bg-[color:var(--kkp-surface)] text-white"
                >
                  {action}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="flex flex-col gap-1 border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3">
            <div className="text-sm font-semibold text-white">
              Audit records
            </div>
            <div className="text-xs text-[color:var(--kkp-text-muted)]">
              Showing {filtered.length} activity record(s).
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[color:var(--kkp-surface-2)]">
                <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                  <th className="px-4 py-3">Seq ID</th>
                  <th className="px-4 py-3">Timestamp</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Details</th>
                  <th className="px-4 py-3">IP Address</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">User Agent</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      No audit records match the current filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((record) => (
                    <tr
                      key={record.seqId}
                      className="border-t border-[color:rgba(255,255,255,0.08)]"
                    >
                      <td className="px-4 py-3 font-semibold text-white">
                        {logSeqNo.get(record.seqId) ?? record.seqId}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {record.timestamp}
                      </td>
                      <td className="px-4 py-3 text-white">{record.actor}</td>
                      <td className="px-4 py-3 text-white">{record.action}</td>
                      <td className="px-4 py-3">
                        <div className="max-w-[320px] truncate text-[color:rgba(255,255,255,0.86)]">
                          {record.details}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {record.ipAddress}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {record.city}, {record.region}, {record.country}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[240px] truncate text-[color:rgba(255,255,255,0.86)]">
                          {record.userAgent}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
